// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import type { Accidental, LetterName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { InteractiveOptions, UserData } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import type { ScaleDegreeNoteIDQuestion } from './scaleDegreeNoteQuestionGenerator';
import {
  generateScaleDegreeNoteQuestions,
  processManualScaleDegreeNoteQuestions,
  getRomanFromScaleDegree,
  scaleDegreeNoteSkillsToAvailableModes,
} from './scaleDegreeNoteQuestionGenerator';
import Note from '../uTheory.Notation/note';
import type { FontKey } from '../uTheory.Font/font';
import { toHTML } from '../uTheory.Font/font';
import { makeString } from '../uTheory.Skills/skills';
import type { SolfegePresentationSystem } from './solfegeDefinitions';
import { Resolution } from '../uTheory.Notation/resolution';
import type LetterPanel from '../uTheory.Notation/letterPanel';

export interface ScaleDegreeNoteOptions extends InteractiveOptions {
  /** Whether numbers (1, 2, #7, etc.) or names (tonic, supertonic, leading tone, etc.) is used */
  presentationSystem?: SolfegePresentationSystem | 'romans';
  /** Changes key after this number of question */
  changeKeyEvery?: number;
  /** Should we identify the key instead of the note */
  keyIdentification?: boolean;
}

export default class ScaleDegreeToNoteID extends InteractivePrototype {
  declare options: ScaleDegreeNoteOptions;

  declare currentQuestion: ScaleDegreeNoteIDQuestion;

  declare questions: ScaleDegreeNoteIDQuestion[];

  private availableModes: (ModeName | 'allMinors')[] = [];

  private solfegeSystem!: SolfegeSystemName;

  declare letterPanel: LetterPanel;

  answer: {
    letter: LetterName | '';
    accidental: Accidental;
  } | null;

  constructor(displayDiv: HTMLDivElement, options: ScaleDegreeNoteOptions, userData?: UserData) {
    super(displayDiv, options, userData);
    this.targetSpeedFromSkills = ScaleDegreeToNoteID.registry.skills;
    this.answer = null;
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: ScaleDegreeNoteOptions, userData?: UserData) {
    options.maxQuestions = options.maxQuestions || 10;

    if (options.drill && !options.skills) {
      throw Error('scaleDegreeToNoteID cannot be initialized in drill mode without skills.');
    }

    if (options.drill && options.skills) {
      const skills = options.skills.map(makeString);
      this.availableModes = scaleDegreeNoteSkillsToAvailableModes(skills);
    }

    // Set whether this interactive uses degree numbers or names
    if (!options.presentationSystem) {
      const useNames = options.skills?.includes('scale_degree_names');

      // We use the student's solfege system if we're training a solfege skill
      const useStudentSolfegeSystem = !!options.skills
        ?.map((skill) => makeString(skill))
        .filter((t): t is string => !!t)
        .find((skill) => skill.includes('labeling'));
      const studentSolfegeSystem = userData?.userStore.preferences.solfegeMethod || 'scaleDegrees';

      if (useNames) options.presentationSystem = 'names';
      else if (useStudentSolfegeSystem) {
        options.presentationSystem =
          studentSolfegeSystem === 'scaleDegrees' ? 'scaleDegrees' : 'solfege';
      } else {
        options.presentationSystem = 'scaleDegrees';
      }
    }

    this.solfegeSystem = userData?.userStore.preferences.solfegeMethod || 'solfegeDoMinor';

    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 'all',
      },
      letters: { letterButtons: true, accidentalButtons: true, checkButton: true },
      checkMark: true,
      player: {},
      progressTracker: true,
    };

    this.layout = {
      instructions: { noWrap: true },
    };

    return super.init(displayDiv, options, userData);
  }

  initializeMusic() {}

  generateQuestions(questions: (string | number)[]) {
    questions = questions || this.options.questions;

    this.questions = this.options.drill
      ? generateScaleDegreeNoteQuestions(
          this.options.maxQuestions,
          this.availableModes,
          this.options.presentationSystem!,
          this.solfegeSystem,
          this.options.changeKeyEvery,
          'write'
        )
      : processManualScaleDegreeNoteQuestions(
          questions,
          this.options.presentationSystem!,
          this.solfegeSystem,
          'write'
        );

    if (this.options.keyIdentification) {
      this.questions.forEach((question, i) => {
        const [degree] = question.text;
        const noteName = question.note.toAccidentalFontShortString();
        const keyQuality = question.key.quality;
        question.text = [keyQuality, noteName, degree as string, `${i + 1}`];
      });
    }

    return this.questions;
  }

  addListeners() {
    this.letterPanel!.addAccidentalCallback(this.addAccidental, this);
    this.letterPanel!.addLetterCallback(this.addNoteLetter, this);
    this.letterPanel!.addCheckCallback(this.checkAnswer, this);
  }

  protected addNoteLetter(letter: LetterName) {
    this.answer!.letter = letter;
    this.displayAnswer();
  }

  protected addAccidental(accidental: Accidental) {
    this.answer!.accidental = accidental;
    this.displayAnswer();
  }

  newQuestion(disableAudio?: any) {
    this.letterPanel.clearNoteName();
    this.questionPrompt = this.getQuestionPrompt();
    super.newQuestion(disableAudio);
    this.answer = { letter: '', accidental: '' };
    this.displayAnswer();
  }

  /**
   * Generate the question prompt specifying the note and key based on the current question
   */
  protected getQuestionPrompt() {
    const testMode = this.optionsTrue('testMode');
    let questionPrompt;

    if (this.options.keyIdentification) {
      questionPrompt = ([quality, noteName, scaleDegree, questionNumber]: string[]) => {
        let text = `What ${quality} key has <br />
        ${noteName} as ${this.options.presentationSystem === 'names' ? 'its' : ''}
        ${scaleDegree}:
        <span id='scaleDegreeToNoteIDAnswerSpan'>___</span> ${(quality as string).slice(0, 3)}`;
        if (!testMode) {
          text += `<br /><small>#${questionNumber} of ${this.questions.length}<small>`;
        }
        return text;
      };
    } else if (this.options.presentationSystem === 'romans') {
      questionPrompt = `What is the root of a <br />
        {question} chord in {question}? <span id='scaleDegreeToNoteIDAnswerSpan'>___</span>`;
      if (testMode === false) {
        questionPrompt += `<small><br />#{question} of ${this.questions.length}<small>`;
      }
    } else {
      questionPrompt = `What is ${
        this.options.presentationSystem === 'names' ? 'the' : ''
      }<br />{question} in {question}: <span id='scaleDegreeToNoteIDAnswerSpan'>___</span>`;
      if (testMode === false) {
        questionPrompt += `<small><br />#{question} of ${this.questions.length}<small>`;
      }
    }

    return questionPrompt;
  }

  /**
   * Updates the __ in the answer space to the note and/or accidental that the student chose.
   */
  protected displayAnswer() {
    const answerDiv = document.getElementById('scaleDegreeToNoteIDAnswerSpan');
    const answer =
      this.answer!.letter +
      toHTML('accidental', this.answer!.accidental as FontKey, { ariaSpaceBefore: true });
    if (!answer) {
      answerDiv!.removeAttribute('aria-live');
    } else {
      answerDiv!.setAttribute('aria-live', 'polite');
    }
    answerDiv!.innerHTML = answer || '__';
  }

  checkAnswer() {
    if (!this.answer!.letter) return;
    if (this.rightAnswerDelay) return;

    let correct;
    let answerNote;
    if (this.options.keyIdentification) {
      answerNote = new Note(`${this.answer!.letter}${this.answer!.accidental}`);
      const correctNote = this.currentQuestion.key.keyNote;
      correct = answerNote.equalsIgnoreOctave(correctNote);
    } else {
      answerNote = new Note(`${this.answer!.letter}${this.answer!.accidental}`);
      const correctNote = this.currentQuestion.note;
      correct = answerNote.equalsIgnoreOctave(correctNote);
    }

    if (this.options.testMode) {
      this.checkpointAnswer({ correct, answer: answerNote });
      return;
    }

    // When correct, call rightAnswer();
    if (correct) {
      this.rightAnswer(500);
      return;
    }
    // When incorrect, call wrongAnswer();
    this.wrongAnswer();
  }

  /**
   * Plays the tonic triad and the question note
   */
  playAnswer() {
    if (this.options.presentationSystem === 'romans') {
      const rn = getRomanFromScaleDegree(this.currentQuestion.degree, this.currentQuestion.key);
      console.log('rn', rn);
      const nh = rn.chord.getOpenSpacing();
      if (!nh) {
        throw new Error(`Unable to get a note holder from the Roman Numeral ${rn.toString()}`);
      }
      this.player!.playMusic(nh);
      return;
    }

    const resolver = new Resolution(this.player!);
    resolver.resolve(this.currentQuestion.key, this.currentQuestion.degree, { tempo: 100 });
  }

  newCheckpointQuestion(questionString: string | number) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.questionNumber = 0;
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  getAnswerAndData(answerObj?: { answer: Note; correct: boolean }) {
    const answerAndData = {
      question: `${this.currentQuestion.key}: ${this.currentQuestion.degree}`,
      questionType: 'ScaleDegreeToNoteID',
      questionText: this.currentInstructions!,
      questionSVG: undefined,
      studentAnswerText: answerObj?.answer ? answerObj.answer.toShortString() : '__',
      studentAnswerSVG: undefined,
      correctAnswerText: this.currentQuestion.note.toShortString(),
      rightAnswers: answerObj?.correct ? 1 : 0,
      wrongAnswers: answerObj?.correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  static registry = {
    description: 'Spell the Scale Degree',
    // only have scale degree names if included in skills
    skills: [
      'scale_degree_conversion_major',
      'scale_degree_conversion_minor',
      'scale_degree_names',
    ],
  };
}
